import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Upload, Building2, FileText, ArrowRight, Shield, ShieldCheck, ShieldOff } from 'lucide-react';

export default function Settings() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    company_name: 'ASSETCO',
    company_logo: null,
    website_logo: null,
    website_logo_size: 96,
    show_company_name: true,
    logo_size: 96,
    document_header: '',
    document_footer: ''
  });
  const [loading, setLoading] = useState(false);

  // TOTP State
  const [totpStatus, setTotpStatus] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [totpDisableCode, setTotpDisableCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [protectedPages, setProtectedPages] = useState(['/accounting', '/loans', '/budget']);
  const [editPagesCode, setEditPagesCode] = useState('');
  const [showEditPages, setShowEditPages] = useState(false);
  const [hasDevice, setHasDevice] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const isAdmin = user?.permissions?.includes('all');

  const ALL_PROTECTABLE_PAGES = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/pos', label: 'POS' },
    { path: '/orders', label: 'Orders' },
    { path: '/inventory', label: 'Inventory' },
    { path: '/quotations', label: 'Quotations' },
    { path: '/invoices', label: 'Invoices' },
    { path: '/credit-invoices', label: 'Credit Invoices' },
    { path: '/costing', label: 'Costing' },
    { path: '/accounting', label: 'Accounting' },
    { path: '/expenses', label: 'Expenses' },
    { path: '/purchase-orders', label: 'Purchase Orders' },
    { path: '/suppliers', label: 'Suppliers' },
    { path: '/budget', label: 'Budget' },
    { path: '/tax', label: 'Tax' },
    { path: '/petty-cash', label: 'Petty Cash' },
    { path: '/loans', label: 'Loans' },
    { path: '/transfers', label: 'Transfers' },
    { path: '/reports', label: 'Reports' },
  ];

  useEffect(() => {
    fetchSettings();
    fetchTotpStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedSettings = {
        website_logo_size: 96,
        show_company_name: true,
        logo_size: 96,
        ...response.data
      };
      setSettings(fetchedSettings);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchTotpStatus = async () => {
    try {
      const res = await axios.get(`${API}/settings/totp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotpStatus(res.data.enabled);
      setHasDevice(res.data.has_device);
      setIsSuperAdmin(res.data.is_super_admin);
      setIsAdminUser(res.data.is_admin);
      if (res.data.protected_pages?.length) {
        setProtectedPages(res.data.protected_pages);
      }
    } catch {}
  };

  const handleTotpSetup = async () => {
    setTotpLoading(true);
    try {
      const res = await axios.post(`${API}/settings/totp/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotpSetupData(res.data);
      toast.success('Scan the QR code with Google Authenticator');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to setup TOTP');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleTotpVerifySetup = async () => {
    if (totpVerifyCode.length < 6) return;
    setTotpLoading(true);
    try {
      await axios.post(`${API}/settings/totp/verify-setup`, {
        code: totpVerifyCode,
        protected_pages: protectedPages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OTP protection enabled!');
      setTotpStatus(true);
      setTotpSetupData(null);
      setTotpVerifyCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleTotpDisable = async () => {
    if (totpDisableCode.length < 6) return;
    setTotpLoading(true);
    try {
      await axios.post(`${API}/settings/totp/disable`, { code: totpDisableCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OTP protection disabled');
      setTotpStatus(false);
      setTotpDisableCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleUpdateProtectedPages = async () => {
    if (editPagesCode.length < 6) return;
    setTotpLoading(true);
    try {
      await axios.put(`${API}/settings/totp/protected-pages`, {
        code: editPagesCode,
        protected_pages: protectedPages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Protected pages updated!');
      setEditPagesCode('');
      setShowEditPages(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setTotpLoading(false);
    }
  };

  const togglePage = (path) => {
    setProtectedPages(prev =>
      prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const handleRemoveDevice = async () => {
    setTotpLoading(true);
    try {
      await axios.delete(`${API}/settings/totp/my-device`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device removed. You can set up a new one.');
      setHasDevice(false);
      setTotpSetupData(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove device');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleImageUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({
        ...settings,
        [field]: response.data.image_url
      });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/company`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear public website cache so changes reflect immediately
      localStorage.removeItem('websiteLogo');
      localStorage.removeItem('companyName');
      localStorage.removeItem('websiteLogoSize');
      localStorage.removeItem('showCompanyName');
      
      toast.success('Settings saved successfully! Changes will appear on website.');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Company Settings</h1>
          <p className="text-slate-600 mt-1">Manage your company branding and logos</p>
        </div>

        {/* Quick Link to Invoice & Quotation Settings */}
        <Card className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-600 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Invoice & Quotation Settings</h3>
                <p className="text-sm text-slate-600">Configure payment methods, terms & conditions</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/invoice-quotation-settings')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Configure
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>

        {/* Quick Link to Currency Settings */}
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-600 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Currency & Exchange Rates</h3>
                <p className="text-sm text-slate-600">Set exchange rates for MVR and CHN</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/currency-settings')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Configure
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Company Information */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-emerald-600" />
              <h2 className="text-xl font-bold text-slate-900">Company Information</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="ASSETCO"
                  data-testid="company-name-input"
                />
              </div>
              
              <div>
                <Label>Store Handler Phone (WhatsApp/Viber)</Label>
                <Input
                  value={settings.store_handler_phone || ''}
                  onChange={(e) => setSettings({ ...settings, store_handler_phone: e.target.value })}
                  placeholder="+960 7614441"
                  data-testid="store-handler-phone-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This number will receive order notifications via WhatsApp/Viber
                </p>
              </div>
            </div>
          </Card>

          {/* Social Media Configuration */}
          <Card className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Social Media & Contact Links</h2>
            <p className="text-sm text-slate-600 mb-4">
              Configure custom links for social media platforms and messaging apps
            </p>
            
            <div className="space-y-4">
              <div>
                <Label>Viber Link</Label>
                <Input
                  value={settings.viber_link || ''}
                  onChange={(e) => setSettings({ ...settings, viber_link: e.target.value })}
                  placeholder="viber://chat?number=%2B9607614441"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Custom Viber deep link (e.g., viber://chat?number=%2B9607614441)
                </p>
              </div>

              <div>
                <Label>WhatsApp Link</Label>
                <Input
                  value={settings.whatsapp_link || ''}
                  onChange={(e) => setSettings({ ...settings, whatsapp_link: e.target.value })}
                  placeholder="https://wa.me/9607614441"
                />
                <p className="text-xs text-slate-500 mt-1">
                  WhatsApp link (e.g., https://wa.me/9607614441)
                </p>
              </div>

              <div>
                <Label>Facebook Messenger Link</Label>
                <Input
                  value={settings.messenger_link || ''}
                  onChange={(e) => setSettings({ ...settings, messenger_link: e.target.value })}
                  placeholder="https://m.me/yourpagename"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Facebook Messenger link (e.g., https://m.me/yourpagename)
                </p>
              </div>

              <div>
                <Label>Instagram Profile Link</Label>
                <Input
                  value={settings.instagram_link || ''}
                  onChange={(e) => setSettings({ ...settings, instagram_link: e.target.value })}
                  placeholder="https://instagram.com/yourprofile"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Instagram profile URL
                </p>
              </div>

              <div>
                <Label>TikTok Profile Link</Label>
                <Input
                  value={settings.tiktok_link || ''}
                  onChange={(e) => setSettings({ ...settings, tiktok_link: e.target.value })}
                  placeholder="https://tiktok.com/@yourprofile"
                />
                <p className="text-xs text-slate-500 mt-1">
                  TikTok profile URL
                </p>
              </div>

              <div>
                <Label>SMS Number</Label>
                <Input
                  value={settings.sms_number || ''}
                  onChange={(e) => setSettings({ ...settings, sms_number: e.target.value })}
                  placeholder="+960 7614441"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Phone number for SMS messages (e.g., sms:+9607614441)
                </p>
              </div>
            </div>
          </Card>

          {/* Review Settings */}
          <Card className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Review Settings</h2>
            <p className="text-sm text-slate-600 mb-4">
              Configure how customers can write reviews for your products
            </p>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enable-open-reviews"
                checked={settings.enable_open_reviews || false}
                onChange={(e) => setSettings({ ...settings, enable_open_reviews: e.target.checked })}
                className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              <div>
                <Label htmlFor="enable-open-reviews" className="cursor-pointer font-medium">
                  Allow Open Reviews
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  When enabled, anyone can write a review. When disabled, only verified purchasers (with order/sale link) can write reviews.
                </p>
              </div>
            </div>
          </Card>

          {/* Company Logo (for invoices, reports) */}
          <Card className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Company Logo</h2>
            <p className="text-sm text-slate-600 mb-4">
              This logo will appear on invoices, quotations, and sales reports
            </p>
            
            <div className="space-y-4">
              {settings.company_logo && (
                <div className="w-full h-48 bg-slate-50 rounded-lg border-2 border-slate-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={settings.company_logo}
                    alt="Company Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'company_logo')}
                  className="hidden"
                  id="company-logo-upload"
                />
                <label htmlFor="company-logo-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => document.getElementById('company-logo-upload').click()}
                    data-testid="upload-company-logo"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Company Logo
                  </Button>
                </label>
              </div>
            </div>
          </Card>

          {/* Website Logo */}
          <Card className="p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Website Logo</h2>
            <p className="text-sm text-slate-600 mb-4">
              This logo will appear on your public website and admin login page
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {settings.website_logo && (
                  <div className="w-full h-48 bg-slate-50 rounded-lg border-2 border-slate-200 flex items-center justify-center overflow-hidden">
                    <img
                      src={settings.website_logo}
                      alt="Website Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
                
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'website_logo')}
                    className="hidden"
                    id="website-logo-upload"
                  />
                  <label htmlFor="website-logo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('website-logo-upload').click()}
                      data-testid="upload-website-logo"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Website Logo
                    </Button>
                  </label>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Logo Guidelines</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Recommended size: 200x200px to 400x400px</li>
                  <li>• Maximum file size: 5MB</li>
                  <li>• Supported formats: PNG, JPG, SVG</li>
                  <li>• Use transparent background (PNG) for best results</li>
                  <li>• Square logos work best</li>
                </ul>
              </div>
            </div>
            
            {/* Website Logo Customization */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">Website Display Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Website Logo Size (pixels)</Label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="40"
                      max="200"
                      value={settings.website_logo_size || 96}
                      onChange={(e) => setSettings({ ...settings, website_logo_size: parseInt(e.target.value) })}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, rgb(16 185 129) 0%, rgb(16 185 129) ${((settings.website_logo_size || 96) - 40) / 160 * 100}%, rgb(226 232 240) ${((settings.website_logo_size || 96) - 40) / 160 * 100}%, rgb(226 232 240) 100%)`
                      }}
                    />
                    <span className="text-sm font-semibold text-slate-900 min-w-[60px] text-right">
                      {settings.website_logo_size || 96}px
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Adjust the logo size on your public website (40-200px)
                  </p>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="show-company-name"
                    checked={settings.show_company_name !== false}
                    onChange={(e) => setSettings({ ...settings, show_company_name: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                  />
                  <div>
                    <Label htmlFor="show-company-name" className="cursor-pointer font-medium">
                      Show Company Name on Website
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Display "{settings.company_name || 'ASSETCO'}" below the logo on your public website
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Document Settings */}
          <Card className="p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Invoice & Quotation Settings</h2>
            
            <div className="space-y-6">
              <div>
                <Label>Logo Size (pixels)</Label>
                <Input
                  type="number"
                  value={settings.logo_size}
                  onChange={(e) => setSettings({ ...settings, logo_size: parseInt(e.target.value) })}
                  min="50"
                  max="200"
                  placeholder="96"
                />
                <p className="text-xs text-slate-500 mt-1">Controls logo size on invoices and quotations (50-200px)</p>
              </div>

              <div>
                <Label>Document Header (Optional)</Label>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md"
                  value={settings.document_header}
                  onChange={(e) => setSettings({ ...settings, document_header: e.target.value })}
                  placeholder="Enter header text that appears at top of invoices and quotations. HTML allowed."
                />
                <p className="text-xs text-slate-500 mt-1">This appears above company logo. You can use HTML for images/formatting.</p>
              </div>

              <div>
                <Label>Document Footer (Optional)</Label>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md"
                  value={settings.document_footer}
                  onChange={(e) => setSettings({ ...settings, document_footer: e.target.value })}
                  placeholder="Enter footer text for invoices and quotations. HTML allowed."
                />
                <p className="text-xs text-slate-500 mt-1">This appears at bottom before thank you message. You can use HTML for images/formatting.</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 font-medium mb-2">💡 Pro Tip: Adding Brand Logos in Header/Footer</p>
                <p className="text-xs text-blue-700">You can add images using HTML:<br/>
                <code className="bg-white px-2 py-1 rounded text-xs">
                  &lt;img src="YOUR_IMAGE_URL" style="height:40px" /&gt;
                </code>
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* TOTP / OTP Security Gate */}
        {isAdminUser && (
          <Card className="p-6" data-testid="totp-settings">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-semibold text-slate-900">OTP Security Gate</h2>
              {totpStatus && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Active
                </span>
              )}
              {hasDevice && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                  Your Device Linked
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {isSuperAdmin
                ? 'Protect pages with Google Authenticator OTP. Set up your device and choose which pages require OTP. All admin users can register their own device.'
                : 'Register your Google Authenticator device to access OTP-protected pages.'
              }
            </p>

            {/* Device Setup - for admins who don't have a device yet */}
            {!hasDevice && !totpSetupData && (
              <Button
                onClick={handleTotpSetup}
                disabled={totpLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="totp-setup-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                {totpLoading ? 'Setting up...' : 'Set Up My Device'}
              </Button>
            )}

            {/* Device already registered - show status and remove option */}
            {hasDevice && !totpSetupData && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Your device is registered</p>
                  <p className="text-xs text-blue-600">Google Authenticator is linked to your account</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveDevice}
                  disabled={totpLoading}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="totp-remove-device"
                >
                  Remove Device
                </Button>
              </div>
            )}

            {totpSetupData && !hasDevice && (
              <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm font-medium text-slate-700">1. Scan this QR code with Google Authenticator</p>
                  <img
                    src={totpSetupData.qr_code}
                    alt="TOTP QR Code"
                    className="w-48 h-48 border rounded-lg"
                    data-testid="totp-qr-code"
                  />
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Or enter this key manually:</p>
                    <code className="text-xs bg-white px-3 py-1 rounded border font-mono select-all">{totpSetupData.secret}</code>
                  </div>
                </div>

                {/* Page checklist - only for super admin during initial setup */}
                {isSuperAdmin && !totpStatus && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">2. Choose which pages to protect</p>
                    <div className="bg-white rounded-lg border p-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {ALL_PROTECTABLE_PAGES.map(pg => (
                          <label
                            key={pg.path}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${
                              protectedPages.includes(pg.path)
                                ? 'bg-emerald-50 text-emerald-800 font-medium'
                                : 'text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={protectedPages.includes(pg.path)}
                              onChange={() => togglePage(pg.path)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            {pg.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    {isSuperAdmin && !totpStatus ? '3.' : '2.'} Enter the 6-digit code to verify & {totpStatus ? 'register device' : 'enable'}
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={totpVerifyCode}
                      onChange={(e) => setTotpVerifyCode(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => e.key === 'Enter' && handleTotpVerifySetup()}
                      className="flex-1 text-center text-xl tracking-[0.3em] font-mono h-12 border rounded-md px-3"
                      data-testid="totp-verify-input"
                    />
                    <Button
                      onClick={handleTotpVerifySetup}
                      disabled={totpLoading || totpVerifyCode.length < 6 || (isSuperAdmin && !totpStatus && protectedPages.length === 0)}
                      className="bg-emerald-600 hover:bg-emerald-700 h-12"
                      data-testid="totp-verify-btn"
                    >
                      {totpLoading ? 'Verifying...' : totpStatus ? 'Register' : 'Enable'}
                    </Button>
                  </div>
                  {isSuperAdmin && !totpStatus && protectedPages.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Select at least one page to protect</p>
                  )}
                </div>
              </div>
            )}

            {totpStatus && (
              <div className="space-y-4">
                {/* Protected Pages Checklist - Super Admin only */}
                {isSuperAdmin && (
                  <div className="border rounded-lg p-4 bg-emerald-50">
                    <p className="text-sm text-emerald-800 flex items-center gap-2 mb-3">
                      <ShieldCheck className="w-4 h-4" />
                      OTP protection is active for <strong>{protectedPages.length}</strong> pages.
                    </p>

                    <div className="bg-white rounded-lg border p-3 mb-3">
                      <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Protected Pages</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {ALL_PROTECTABLE_PAGES.map(pg => (
                          <label
                            key={pg.path}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${
                              protectedPages.includes(pg.path)
                                ? 'bg-emerald-50 text-emerald-800 font-medium'
                                : 'text-slate-500 hover:bg-slate-50'
                            }`}
                            data-testid={`otp-page-${pg.path.replace('/', '')}`}
                          >
                            <input
                              type="checkbox"
                              checked={protectedPages.includes(pg.path)}
                              onChange={() => togglePage(pg.path)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            {pg.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {!showEditPages ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEditPages(true)}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                        data-testid="otp-save-pages-btn"
                      >
                        Save Page Changes
                      </Button>
                    ) : (
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <label className="text-xs text-slate-600 mb-1 block">Enter OTP to confirm changes</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="000000"
                            value={editPagesCode}
                            onChange={(e) => setEditPagesCode(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateProtectedPages()}
                            className="w-full text-center text-lg tracking-[0.3em] font-mono h-10 border rounded-md px-3"
                            autoFocus
                            data-testid="otp-save-pages-code"
                          />
                        </div>
                        <Button
                          onClick={handleUpdateProtectedPages}
                          disabled={totpLoading || editPagesCode.length < 6}
                          className="bg-emerald-600 hover:bg-emerald-700 h-10"
                          data-testid="otp-save-pages-confirm"
                        >
                          {totpLoading ? '...' : 'Confirm'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setShowEditPages(false); setEditPagesCode(''); fetchTotpStatus(); }}
                          className="h-10"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* For non-super admins, just show which pages are protected */}
                {!isSuperAdmin && (
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-sm text-slate-700 mb-2 font-medium">Protected pages:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {protectedPages.map(p => {
                        const pg = ALL_PROTECTABLE_PAGES.find(a => a.path === p);
                        return pg ? (
                          <span key={p} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">{pg.label}</span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Disable Section - Super Admin only */}
                {isSuperAdmin && (
                  <div className="flex gap-2 items-end border rounded-lg p-3 bg-red-50">
                    <div className="flex-1">
                      <label className="text-xs text-red-600 mb-1 block font-medium">Enter OTP to disable protection</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        value={totpDisableCode}
                        onChange={(e) => setTotpDisableCode(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => e.key === 'Enter' && handleTotpDisable()}
                        className="w-full text-center text-lg tracking-[0.3em] font-mono h-10 border rounded-md px-3"
                        data-testid="totp-disable-input"
                      />
                    </div>
                    <Button
                      onClick={handleTotpDisable}
                      disabled={totpLoading || totpDisableCode.length < 6}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-100 h-10"
                      data-testid="totp-disable-btn"
                    >
                      <ShieldOff className="w-4 h-4 mr-1" />
                      {totpLoading ? '...' : 'Disable'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 px-8"
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
